import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Lock } from "lucide-react";
import { CABINET } from "@/lib/content";

const NAV = [
    { to: "/", label: "Accueil" },
    { to: "/domaines", label: "Domaines" },
    { to: "/cabinet", label: "Le cabinet" },
    { to: "/journal", label: "Journal" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <motion.header
            data-testid="site-navbar"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
            style={{
                position: "fixed",
                top: "var(--banner-height, 0px)",
                left: 0,
                right: 0,
                zIndex: 90,
                backdropFilter: scrolled ? "blur(14px)" : "blur(0px)",
                WebkitBackdropFilter: scrolled ? "blur(14px)" : "blur(0px)",
                background: scrolled ? "rgba(255, 255, 255, 0.85)" : "transparent",
                borderBottom: scrolled ? "1px solid var(--hairline)" : "1px solid transparent",
                transition: "all 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)",
            }}
        >
            <div
                className="mx-auto flex items-center justify-between"
                style={{
                    maxWidth: 1400,
                    padding: scrolled ? "14px 40px" : "22px 40px",
                    transition: "padding 0.4s ease",
                }}
            >
                <Link
                    to="/"
                    data-testid="navbar-logo"
                    className="flex items-center gap-3"
                    style={{ textDecoration: "none" }}
                >
                    <img
                        src="/assets/emblem.png"
                        alt="ANA Corps & Souffle"
                        style={{ height: scrolled ? 34 : 42, transition: "height 0.4s ease" }}
                    />
                    <div className="hidden md:block" style={{ lineHeight: 1.1 }}>
                        <div
                            className="font-display"
                            style={{
                                fontSize: 16,
                                fontWeight: 500,
                                color: "var(--forest-dark)",
                                letterSpacing: "0.02em",
                            }}
                        >
                            ANA Corps & Souffle
                        </div>
                        <div
                            style={{
                                fontSize: 10,
                                textTransform: "uppercase",
                                letterSpacing: "0.18em",
                                color: "var(--ink-soft)",
                                marginTop: 2,
                            }}
                        >
                            Cabinet de physiothérapie
                        </div>
                    </div>
                </Link>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
                    {NAV.map((item, i) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`navlink-${item.to.replace("/", "") || "home"}`}
                            className={({ isActive }) =>
                                `link-underline text-[13px] tracking-wide ${
                                    isActive ? "font-medium" : "font-normal"
                                }`
                            }
                            style={{ color: "var(--forest-dark)" }}
                        >
                            <span
                                style={{
                                    fontSize: 10,
                                    color: "var(--terra)",
                                    marginRight: 8,
                                    fontWeight: 500,
                                }}
                            >
                                0{i + 1}
                            </span>
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={`tel:${CABINET.phoneIntl}`}
                        data-testid="navbar-phone-link"
                        className="hidden xl:inline-flex items-center gap-2 btn-ghost"
                    >
                        <Phone size={14} />
                        <span>{CABINET.phone}</span>
                    </a>
                    <Link
                        to="/rendez-vous"
                        data-testid="navbar-cta-appointment"
                        className="hidden lg:inline-flex btn-primary"
                    >
                        Prendre rendez-vous
                    </Link>
                    <Link
                        to="/admin"
                        data-testid="navbar-admin-link"
                        aria-label="Espace administrateur"
                        title="Espace administrateur"
                        className="hidden lg:inline-flex items-center justify-center"
                        style={{
                            width: 40, height: 40, borderRadius: "50%",
                            border: "1px solid var(--terra-light)",
                            background: "var(--cream-warm)",
                            color: "var(--forest-dark)",
                            transition: "all 0.25s ease",
                        }}
                    >
                        <Lock size={14} />
                    </Link>
                    <button
                        data-testid="navbar-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden p-2"
                        style={{ background: "transparent", border: "none" }}
                        aria-label="Menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {/* Mobile nav */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="navbar-mobile-panel"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4 }}
                        style={{
                            background: "var(--cream)",
                            borderBottom: "1px solid var(--hairline)",
                            overflow: "hidden",
                        }}
                    >
                        <div className="flex flex-col gap-2 p-8">
                            {NAV.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className="font-display text-2xl py-2"
                                    style={{ color: "var(--forest-dark)" }}
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            <div className="mt-4 divider-terra" />
                            <a
                                href={`tel:${CABINET.phoneIntl}`}
                                className="mt-4 btn-ghost self-start"
                            >
                                <Phone size={14} /> {CABINET.phone}
                            </a>
                            <Link to="/rendez-vous" className="mt-2 btn-primary self-start">
                                Prendre rendez-vous
                            </Link>
                            <Link
                                to="/admin"
                                data-testid="navbar-mobile-admin-link"
                                className="mt-6 btn-ghost self-start"
                            >
                                <Lock size={14} /> Espace administrateur
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
